/**
 * СЛОТИ ЧЕРНЕТКИ КАРТКИ (ч.1060, вимога власника: «текст у рядку «додати»
 * до натискання кнопки і галочка «Контроль підписання» губляться при
 * перемиканні вкладки — це треба полагодити, а не писати в техборг»).
 *
 * Незбережений ввід, що живе ВСЕРЕДИНІ вкладки картки (рядок «додати» до
 * натискання кнопки, форма редагування складу / каси / ціни / серії,
 * відкладена галочка), тримаємо не у компоненті вкладки, а у КАРТЦІ:
 *  • при перемиканні вкладки компонент розмонтовується, а ввід лишається;
 *  • слоти входять у чернетку картки (useFormDraft) — ввід переживає вихід
 *    зі сторінки, оновлення сторінки і закриття вкладки браузера;
 *  • незавершений рядок позначає себе маркером `pending:<ключ>` — перед
 *    «зберегти» картка питає (confirmPendingRows), щоб такий рядок не зник
 *    мовчки разом із закриттям картки.
 *
 * Поза карткою (без провайдера) useDraftSlot поводиться як звичайний
 * useState — компоненти можна використовувати будь-де.
 */
import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
  type Dispatch, type ReactNode, type SetStateAction,
} from 'react';

export type DraftSlots = Record<string, unknown>;

type SlotSetter = (key: string, update: unknown, initial: unknown) => void;

const SlotsValue = createContext<DraftSlots | null>(null);
const SlotsSetter = createContext<SlotSetter | null>(null);

const own = (o: object, k: string) => Object.prototype.hasOwnProperty.call(o, k);

/** Картка надає слоти своїм вкладкам (стан слотів живе у картці). */
export function DraftSlotsProvider({ slots, setSlots, children }: {
  slots: DraftSlots;
  setSlots: Dispatch<SetStateAction<DraftSlots>>;
  children: ReactNode;
}) {
  // Сетер стабільний (залежить лише від setSlots) — компоненти можуть
  // безпечно тримати його в залежностях ефектів.
  const setter = useCallback<SlotSetter>((key, update, initial) => {
    setSlots((prev) => {
      const has = own(prev, key);
      const cur = has ? prev[key] : initial;
      const next = typeof update === 'function'
        ? (update as (p: unknown) => unknown)(cur)
        : update;
      if (Object.is(cur, next)) return prev;
      return { ...prev, [key]: next };
    });
  }, [setSlots]);
  return (
    <SlotsSetter.Provider value={setter}>
      <SlotsValue.Provider value={slots}>{children}</SlotsValue.Provider>
    </SlotsSetter.Provider>
  );
}

/**
 * Як useState, але у картці значення живе у слоті картки (переживає
 * перемикання вкладок і потрапляє у чернетку). `key` — унікальний у межах
 * картки (префікс компонента: `bankTab.iban`, `orgWarehouses.editing`).
 */
export function useDraftSlot<T>(key: string, initial: T): [T, Dispatch<SetStateAction<T>>] {
  const slots = useContext(SlotsValue);
  const setter = useContext(SlotsSetter);
  const [local, setLocal] = useState<T>(initial);
  const initialRef = useRef(initial);
  const setSlot = useCallback<Dispatch<SetStateAction<T>>>(
    (u) => setter?.(key, u, initialRef.current),
    [setter, key],
  );
  if (!slots || !setter) return [local, setLocal];
  const value = own(slots, key) ? (slots[key] as T) : initial;
  return [value, setSlot];
}

/**
 * Маркер незавершеного рядка: `label` — людська назва, поки у рядку є
 * незбережений ввід («новий банківський рахунок»), `null` — коли порожньо.
 * Маркер лишається у слотах і після розмонтування вкладки — картка знає
 * про недодані рядки, навіть якщо користувач уже на іншій вкладці.
 */
export function usePendingMarker(key: string, label: string | null): void {
  const setter = useContext(SlotsSetter);
  useEffect(() => {
    setter?.(`pending:${key}`, label, null);
  }, [setter, key, label]);
}

/** Людські назви незавершених рядків картки. */
export function pendingRows(slots: DraftSlots): string[] {
  return Object.entries(slots)
    .filter(([k, v]) => k.startsWith('pending:') && typeof v === 'string' && v !== '')
    .map(([, v]) => v as string);
}

type ConfirmLike = (opts: {
  title?: string; message: string; okLabel?: string; cancelLabel?: string;
}) => Promise<boolean>;

/**
 * Перед «зберегти» картки: якщо є недодані рядки — питаємо, щоб вони не
 * зникли мовчки (картка після збереження закривається). true — зберігати.
 */
export async function confirmPendingRows(
  slots: DraftSlots, confirm: ConfirmLike,
): Promise<boolean> {
  const rows = pendingRows(slots);
  if (rows.length === 0) return true;
  return confirm({
    title: 'Є недодані рядки',
    message: `Не додано: ${rows.join('; ')}.\n`
      + 'Щоб зберегти їх, натисніть «додати» або «зберегти» у самому рядку. '
      + 'Зберегти картку без них?',
    okLabel: 'зберегти без них',
    cancelLabel: 'повернутись',
  });
}

/**
 * Чернетка, збережена ДО появи складеного формату (лише обʼєкт форми), —
 * у формат `{ form, … }`. Потрібно, щоб незбережене у браузерах
 * користувачів не загубилось після оновлення системи.
 */
export function withLegacyForm<T extends { form: unknown }>(v: unknown): Partial<T> {
  if (v && typeof v === 'object' && 'form' in (v as object)) return v as Partial<T>;
  return { form: v } as Partial<T>;
}
